// @ts-nocheck
/**
 * Wishlist Manager Module
 * Thin wrapper around the Wishlist Hero JS SDK.
 *
 * Public API mirrors the previous Swym-backed module so call sites stay
 * unchanged. Internally it talks to window.WishListHero_SDK.
 */

var WishlistManager = (function () {
  "use strict";

  var _sdk = null;
  var _wishlistHash = null;
  var _hashPromise = null;
  var _initialized = false;

  // localStorage key holding the latest known wishlist count. Unlike the
  // per-page in-memory SDK cache, this is shared across pages, so it stays
  // correct even when a page is frozen in the back/forward cache (bfcache).
  var WISHLIST_SIG_KEY = "bodiskin:wishlist-count";
  // The count the *current* page's DOM currently reflects. Compared against the
  // shared localStorage value on bfcache restore to detect stale state.
  var _wishlistSig = null;

  // sessionStorage flag handed to the *next* page load so it knows to fade itself
  // back in (see the inline reveal snippet in layout/theme.liquid <head>). Set by
  // fadeAndReload() just before it reloads a stale bfcache page.
  var RELOAD_FADE_KEY = "bodiskin:wl-reload-fade";

  // localStorage ledger of server-acknowledged adds/removes that the Wishlist
  // Hero cache may not reflect yet. The app embed GETs the wishlist on every
  // page load and OVERWRITES its localStorage cache with the response; when
  // that GET was issued before a just-made mutation resolved, it writes the
  // pre-mutation list back (a removed item reappears in the cache). The SDK has
  // no force-refresh, so every read goes through getEffectiveItems(), which
  // re-applies this ledger on top of the cache.
  var WISHLIST_OPS_KEY = "bodiskin:wishlist-ops";
  // By this age any page-load GET reflects the change server-side; expiring
  // keeps a bad op (e.g. acknowledged but never committed) from sticking.
  var OPS_TTL_MS = 10 * 60 * 1000;
  var OPS_MAX = 50;

  function log(message, data) {
    var timestamp = new Date().toISOString();
    console.log("[WishlistManager " + timestamp + "] " + message, data || "");
  }

  /**
   * Record the count the current page now shows, both in memory and in shared
   * localStorage, so other pages (and bfcache restores) can detect changes.
   */
  function persistSig(count) {
    _wishlistSig = String(Math.max(0, count));
    try {
      localStorage.setItem(WISHLIST_SIG_KEY, _wishlistSig);
    } catch (e) {}
  }

  function checkSdkReady() {
    if (_sdk) return _sdk;
    if (window.WishListHero_SDK) {
      _sdk = window.WishListHero_SDK;
      return _sdk;
    }
    return null;
  }

  function loadOps() {
    var raw;
    try {
      raw = localStorage.getItem(WISHLIST_OPS_KEY);
    } catch (e) {
      return [];
    }
    if (!raw) return [];
    var ops;
    try {
      ops = JSON.parse(raw);
    } catch (e) {
      return [];
    }
    if (!Array.isArray(ops)) return [];
    var cutoff = Date.now() - OPS_TTL_MS;
    return ops.filter(function (op) {
      return (
        op &&
        op.v &&
        (op.op === "add" || op.op === "remove") &&
        op.ts > cutoff
      );
    });
  }

  function saveOps(ops) {
    try {
      localStorage.setItem(
        WISHLIST_OPS_KEY,
        JSON.stringify(ops.slice(-OPS_MAX)),
      );
    } catch (e) {}
  }

  /**
   * Record a server-acknowledged add/remove. A newer op on the same variant
   * replaces the older one, so remove-then-re-add nets out to a single add op.
   * For adds, `item` carries SDK-shaped product info so the wishlist page can
   * render the item even while the SDK cache doesn't contain it yet.
   */
  function registerOp(variantId, opType, item) {
    var v = String(variantId);
    var ops = loadOps().filter(function (op) {
      return String(op.v) !== v;
    });
    ops.push({ v: v, op: opType, ts: Date.now(), item: item || null });
    saveOps(ops);
  }

  /**
   * Wishlist Hero resolves its mutation promises even on logical failure and
   * reports it via a status field on the result (400 wrong hash, 403/404/500).
   * The "item already exists" duplicate response comes back with status 200,
   * so it still counts as acknowledged.
   */
  function looksLikeFailure(response) {
    return !!(
      response &&
      typeof response.status === "number" &&
      response.status >= 400
    );
  }

  /**
   * The wishlist as the customer actually left it: the SDK's cached items with
   * the pending-ops ledger applied on top (pending removes dropped, pending
   * adds re-inserted). Idempotent — an op the cache already reflects changes
   * nothing. Returns null when the SDK cache isn't readable yet, so callers
   * can fall back or skip painting rather than flash a wrong empty state.
   */
  function getEffectiveItems() {
    var sdk = checkSdkReady();
    if (!sdk || typeof sdk.GetWishListItems !== "function") return null;
    var list;
    try {
      list = sdk.GetWishListItems();
    } catch (e) {
      return null;
    }
    // Non-array means the cache key was never written (the SDK returns "" or
    // an error object) — nothing trustworthy to reconcile against yet.
    if (!Array.isArray(list)) return null;

    var ops = loadOps();
    if (!ops.length) return list.slice();

    var removed = {};
    var added = {};
    ops.forEach(function (op) {
      if (op.op === "remove") removed[String(op.v)] = true;
      else added[String(op.v)] = op;
    });

    var effective = list.filter(function (item) {
      return !removed[String(item.ProductVariantId)];
    });
    Object.keys(added).forEach(function (v) {
      var present = effective.some(function (item) {
        return String(item.ProductVariantId) === v;
      });
      if (!present) {
        effective.push(added[v].item || { ProductVariantId: Number(v) || v });
      }
    });
    return effective;
  }

  /**
   * Whether a variant is on the effective wishlist. Returns null when the SDK
   * cache isn't readable yet (callers should skip or fall back, not clear
   * heart state on null).
   */
  function isInWishlist(variantId) {
    var items = getEffectiveItems();
    if (items === null) return null;
    var v = String(variantId);
    return items.some(function (item) {
      return String(item.ProductVariantId) === v;
    });
  }

  /**
   * Resolve (and cache) the wishlist hash. Creates the wishlist if missing.
   */
  function ensureHash() {
    if (_wishlistHash) return Promise.resolve(_wishlistHash);
    if (_hashPromise) return _hashPromise;

    var sdk = checkSdkReady();
    if (!sdk) return Promise.reject(new Error("Wishlist Hero SDK not ready"));

    _hashPromise = Promise.resolve(sdk.GetWishListHash(true))
      .then(function (hash) {
        _wishlistHash = hash;
        return hash;
      })
      .catch(function (err) {
        _hashPromise = null;
        throw err;
      });
    return _hashPromise;
  }

  /**
   * Adjust the displayed wishlist badge count by a delta (e.g. +1 or -1)
   * without re-fetching, avoiding cache staleness on quick add/remove.
   */
  function adjustWishlistCount(delta) {
    var newCount = null;
    var badges = document.querySelectorAll(".wishlist-count");
    badges.forEach(function (badge) {
      var current = parseInt(badge.textContent.replace(/[^0-9]/g, ""), 10) || 0;
      newCount = Math.max(0, current + delta);
      badge.textContent = "(" + newCount + ")";
    });
    if (newCount === null) {
      // No badge in the DOM on this page — derive from the shared value so the
      // persisted signature still moves in step.
      var stored = parseInt(localStorage.getItem(WISHLIST_SIG_KEY), 10);
      newCount = Math.max(0, (isNaN(stored) ? 0 : stored) + delta);
    }
    persistSig(newCount);
  }

  /**
   * Refresh the wishlist count badge. Counts the *effective* items (SDK cache
   * + pending-ops ledger) instead of trusting sdk.GetWishListItemsCount() —
   * the app embed can overwrite its cache with a pre-mutation server snapshot
   * on page load, which would resurrect a just-removed item's count.
   */
  function updateWishlistCount(sdk) {
    if (!sdk) {
      sdk = checkSdkReady();
      if (!sdk) return;
    }
    try {
      var items = getEffectiveItems();
      // Cache not populated yet (fresh browser, or the page-load GET hasn't
      // landed): keep the sig-painted badge instead of flashing a wrong 0.
      if (items === null) return;
      var count = Math.max(0, items.length);
      var badges = document.querySelectorAll(".wishlist-count");
      badges.forEach(function (badge) {
        badge.textContent = "(" + count + ")";
      });
      persistSig(count);
    } catch (e) {
      log("updateWishlistCount() - EXCEPTION", e);
    }
  }

  /**
   * Re-paint the count badge straight from the shared localStorage value.
   * Used on bfcache restore, where the frozen DOM still shows the old count and
   * the in-memory SDK cache is stale — but localStorage was kept current by
   * whichever page last changed the wishlist. Reload-free and idempotent, so it
   * is safe to run on every pageshow (including Safari/iOS, where
   * event.persisted is unreliable).
   */
  function syncCountFromStorage() {
    var stored;
    try {
      stored = localStorage.getItem(WISHLIST_SIG_KEY);
    } catch (e) {
      return;
    }
    if (stored === null) return;
    var count = parseInt(stored, 10);
    if (isNaN(count)) return;
    count = Math.max(0, count);
    document.querySelectorAll(".wishlist-count").forEach(function (badge) {
      badge.textContent = "(" + count + ")";
    });
    _wishlistSig = String(count);
  }

  /**
   * Reload the page, but hide the reload behind a quick fade so the customer
   * never sees the jarring "page reappears from bfcache, then suddenly reloads"
   * flash. We fade the current page out to the theme background, flag the reload
   * in sessionStorage, then reload; the reloaded page reads that flag (inline
   * snippet in layout/theme.liquid <head>) and fades itself back in from the same
   * background, so the whole swap reads as one smooth cross-fade.
   */
  function fadeAndReload() {
    try {
      sessionStorage.setItem(RELOAD_FADE_KEY, "1");
    } catch (e) {}

    // Match the overlay to whatever the page is actually painted on so the fade
    // dissolves into the background instead of flashing a wrong colour.
    var bg = "#ffffff";
    try {
      var painted = getComputedStyle(document.body).backgroundColor;
      if (painted && painted !== "rgba(0, 0, 0, 0)" && painted !== "transparent") {
        bg = painted;
      }
    } catch (e) {}

    var overlay = document.createElement("div");
    overlay.setAttribute("aria-hidden", "true");
    var s = overlay.style;
    s.position = "fixed";
    s.top = s.right = s.bottom = s.left = "0";
    s.zIndex = "2147483647";
    s.background = bg;
    s.opacity = "0";
    s.transition = "opacity 200ms ease";
    s.pointerEvents = "none";
    document.body.appendChild(overlay);

    var reloaded = false;
    function go() {
      if (reloaded) return;
      reloaded = true;
      window.location.reload();
    }

    // Force a reflow so the browser registers opacity:0 as the starting point
    // before we flip to 1 — otherwise the two writes can coalesce and skip the
    // transition entirely.
    void overlay.offsetWidth;
    overlay.style.opacity = "1";
    overlay.addEventListener("transitionend", go);
    // Fallback: fire even if transitionend never arrives (reduced-motion,
    // background tab, etc.) so a stale page is never left un-reloaded.
    setTimeout(go, 320);
  }

  /**
   * Check if at least one item is on the wishlist.
   * @returns {Promise<boolean>}
   */
  function checkListExists(sdk) {
    return new Promise(function (resolve) {
      if (!sdk) sdk = checkSdkReady();
      if (!sdk) return resolve(false);
      try {
        var items = getEffectiveItems();
        resolve(Array.isArray(items) && items.length > 0);
      } catch (e) {
        resolve(false);
      }
    });
  }

  function updateButtonState(button, isAdded) {
    if (isAdded) {
      button.classList.add("swym-added");
    } else {
      button.classList.remove("swym-added");
    }
  }

  function updateAllMatchingButtons(productId, variantId, isAdded) {
    var selector =
      '.wishlist.swym-wishlist-button[data-product-id="' +
      productId +
      '"][data-variant-id="' +
      variantId +
      '"]';

    document.querySelectorAll(selector).forEach(function (btn) {
      updateButtonState(btn, isAdded);
    });
  }

  /**
   * Build the Wishlist Hero productInfo payload from button-style data.
   * Accepts the same shape the click handler used to send to Swym
   * (empi/epi/du/dt/iu/pr) so call sites can stay unchanged.
   */
  function buildProductInfo(productData, hash) {
    var info = {
      ProductId: Number(productData.empi || productData.ProductId),
      ProductVariantId: Number(productData.epi || productData.ProductVariantId),
      ProductLink: productData.du || productData.ProductLink || "",
      ProductTitle: productData.dt || productData.ProductTitle || "",
      ProductImage: productData.iu || productData.ProductImage || "",
      ProductPrice: Number(productData.pr || productData.ProductPrice) || 0,
      WishlistHash: hash,
    };
    return info;
  }

  /**
   * Add a product to the wishlist.
   * @param {object} productData
   * @param {object} [opts] - Pass { skipUiUpdate: true } when the caller has
   *   already applied the button/count change optimistically.
   */
  function addToWishlist(productData, opts) {
    return new Promise(function (resolve, reject) {
      var sdk = checkSdkReady();

      if (!sdk) {
        var retryCount = 0;
        var maxRetries = 10;
        var retryInterval = setInterval(function () {
          retryCount++;
          sdk = checkSdkReady();
          if (sdk) {
            clearInterval(retryInterval);
            performAdd(sdk, productData, opts, resolve, reject);
          } else if (retryCount >= maxRetries) {
            clearInterval(retryInterval);
            reject(
              new Error(
                "Wishlist Hero SDK not available after " +
                  maxRetries +
                  " retries",
              ),
            );
          }
        }, 500);
        return;
      }

      performAdd(sdk, productData, opts, resolve, reject);
    });
  }

  function performAdd(sdk, productData, opts, resolve, reject) {
    var skipUiUpdate = opts && opts.skipUiUpdate;
    ensureHash()
      .then(function (hash) {
        var info = buildProductInfo(productData, hash);
        return Promise.resolve(sdk.AddWishListItem(info)).then(function (
          response,
        ) {
          // The SDK resolves even on logical failure (wrong hash etc.) and
          // reports it in response.status — reject so optimistic UI rolls back.
          if (looksLikeFailure(response)) {
            throw new Error(
              "Wishlist Hero add failed with status " + response.status,
            );
          }
          log("performAdd() - SUCCESS", response);
          registerOp(info.ProductVariantId, "add", {
            ProductId: info.ProductId,
            ProductVariantId: info.ProductVariantId,
            ProductLink: info.ProductLink,
            ProductTitle: info.ProductTitle,
            ProductName: info.ProductTitle,
            ProductImage: info.ProductImage,
            Price: info.ProductPrice,
          });
          if (!skipUiUpdate) {
            updateAllMatchingButtons(
              info.ProductId,
              info.ProductVariantId,
              true,
            );
            adjustWishlistCount(+1);
          }
          resolve(response);
        });
      })
      .catch(function (error) {
        log("performAdd() - ERROR", error);
        reject(error);
      });
  }

  /**
   * Remove a product from the wishlist.
   * @param {object} productData
   * @param {object} [opts] - Pass { skipUiUpdate: true } when the caller has
   *   already applied the button/count change optimistically.
   */
  function removeFromWishlist(productData, opts) {
    var skipUiUpdate = opts && opts.skipUiUpdate;
    return new Promise(function (resolve, reject) {
      var sdk = checkSdkReady();
      if (!sdk) {
        reject(new Error("Wishlist Hero SDK not ready"));
        return;
      }

      ensureHash()
        .then(function (hash) {
          var variantId = Number(
            productData.epi || productData.ProductVariantId,
          );
          var productId = Number(productData.empi || productData.ProductId);
          return Promise.resolve(
            sdk.DeleteWishListItem({
              ProductVariantId: variantId,
              WishlistHash: hash,
            }),
          ).then(function (response) {
            if (looksLikeFailure(response)) {
              throw new Error(
                "Wishlist Hero delete failed with status " + response.status,
              );
            }
            log("removeFromWishlist() - SUCCESS", response);
            registerOp(variantId, "remove");
            if (!skipUiUpdate) {
              updateAllMatchingButtons(productId, variantId, false);
              adjustWishlistCount(-1);
            }
            resolve(response);
          });
        })
        .catch(function (error) {
          log("removeFromWishlist() - ERROR", error);
          reject(error);
        });
    });
  }

  function handleButtonClick(button) {
    var productData = {
      empi: parseInt(button.getAttribute("data-product-id"), 10),
      epi: parseInt(button.getAttribute("data-variant-id"), 10),
      du: button.getAttribute("data-product-url"),
      dt: button.getAttribute("data-product-title") || "",
      iu: button.getAttribute("data-product-image") || "",
      pr: parseFloat(button.getAttribute("data-product-price")) || 0,
    };

    var isAdded = button.classList.contains("swym-added");
    log(
      "handleButtonClick() - " + (isAdded ? "Removing" : "Adding"),
      productData,
    );

    // Optimistic UI: flip the button + count immediately so the click feels
    // instant, then run the network request in the background. Roll back only
    // if the request actually fails.
    var willBeAdded = !isAdded;
    updateAllMatchingButtons(productData.empi, productData.epi, willBeAdded);
    adjustWishlistCount(willBeAdded ? +1 : -1);

    function rollback(error) {
      updateAllMatchingButtons(productData.empi, productData.epi, isAdded);
      adjustWishlistCount(willBeAdded ? -1 : +1);
      console.warn(
        "Failed to " +
          (isAdded ? "remove from" : "add to") +
          " wishlist (reverted):",
        error,
      );
    }

    if (isAdded) {
      removeFromWishlist(productData, { skipUiUpdate: true }).catch(rollback);
    } else {
      addToWishlist(productData, { skipUiUpdate: true }).catch(rollback);
    }
  }

  /**
   * Sync all PDP wishlist buttons to a new variant and update their visual state.
   */
  function syncButtonsToVariant(variantId) {
    var buttons = document.querySelectorAll(".wishlist.swym-wishlist-button");
    if (!buttons.length) return;

    buttons.forEach(function (btn) {
      btn.setAttribute("data-variant-id", variantId);
    });

    var sdk = checkSdkReady();
    if (!sdk) return;

    try {
      var items = getEffectiveItems();
      var list = Array.isArray(items) ? items : [];
      var found = list.some(function (item) {
        return String(item.ProductVariantId) === String(variantId);
      });
      buttons.forEach(function (btn) {
        updateButtonState(btn, found);
      });
    } catch (e) {
      log("syncButtonsToVariant() - EXCEPTION", e);
    }
  }

  function initSdk(sdk) {
    _sdk = sdk;
    log("initSdk() - Cached Wishlist Hero SDK instance");

    // Initial count refresh
    updateWishlistCount(sdk);

    // The app embed refreshes its localStorage cache from the server shortly
    // after page load (and there is no event when that write lands). Re-derive
    // the badge a couple of times so a late cache write — fresh data from
    // another device, or the first population of an empty cache — still gets
    // painted. Recomputing is idempotent, so extra runs are harmless.
    setTimeout(function () {
      updateWishlistCount();
    }, 2500);
    setTimeout(function () {
      updateWishlistCount();
    }, 7000);

    // Repaint on the SDK's own mutation event, fired by adds/removes from any
    // code path (ours included, and the app's own UI). Deferred a tick because
    // the SDK dispatches the first add event *before* writing its cache, and a
    // single mutation fires the event at least twice — coalesce the burst.
    var updatedQueued = false;
    document.addEventListener("wishlist-hero-wishlist-updated", function () {
      if (updatedQueued) return;
      updatedQueued = true;
      setTimeout(function () {
        updatedQueued = false;
        updateWishlistCount();
      }, 50);
    });

    // Warm the hash cache
    ensureHash().catch(function () {});

    // Sync wishlist buttons when the selected variant changes on PDP
    if (
      typeof subscribe !== "undefined" &&
      typeof PUB_SUB_EVENTS !== "undefined"
    ) {
      subscribe(PUB_SUB_EVENTS.variantChange, function (event) {
        var variant = event.data && event.data.variant;
        if (!variant) return;
        syncButtonsToVariant(variant.id);
      });
    }
  }

  function init() {
    if (_initialized) return;
    _initialized = true;

    log("init() - Initializing WishlistManager");

    // Paint the badge from the shared localStorage count right away, instead
    // of leaving the server-rendered "(0)" on screen until the (slow, deferred)
    // app SDK loads and reports the real count — that late correction reads as
    // a flash. The SDK refresh in initSdk() still runs and wins later.
    syncCountFromStorage();

    var bound = false;
    function bindIfReady() {
      if (bound) return;
      var sdk = checkSdkReady();
      if (!sdk) return;
      bound = true;
      initSdk(sdk);
    }

    // Handle race: SDK may have loaded before this script
    bindIfReady();

    // Otherwise wait for the SDK ready event
    document.addEventListener("wishlist-hero-wishlist-sdk-ready", bindIfReady);

    // Belt-and-braces: poll for a short period in case the event already fired
    if (!bound) {
      var pollAttempts = 0;
      var maxPollAttempts = 30;
      var pollInterval = setInterval(function () {
        pollAttempts++;
        if (checkSdkReady()) {
          clearInterval(pollInterval);
          bindIfReady();
        } else if (pollAttempts >= maxPollAttempts) {
          clearInterval(pollInterval);
          log(
            "init() - WARNING: Wishlist Hero SDK not loaded after " +
              maxPollAttempts +
              " attempts",
          );
        }
      }, 500);
    }

    // Global click handler for wishlist buttons (bubble phase)
    document.addEventListener("click", function (e) {
      var button = e.target.closest(".wishlist.swym-wishlist-button");
      if (!button) return;

      e.preventDefault();
      e.stopImmediatePropagation();

      handleButtonClick(button);
    });

    // Back/forward cache fix. A page restored from bfcache keeps its frozen DOM
    // and a stale in-memory SDK cache, and init()/DOMContentLoaded do NOT run
    // again, so a wishlist change made on another page (e.g. removing an item on
    // the wishlist page) isn't reflected here.
    window.addEventListener("pageshow", function (event) {
      // What was the page displaying BEFORE we touch anything?
      var badge = document.querySelector(".wishlist-count");
      var shownBefore = badge
        ? String(parseInt(badge.textContent.replace(/[^0-9]/g, ""), 10) || 0)
        : null;

      // Re-paint the badge from the shared, cross-page localStorage count. This
      // alone fixes the number on a normal bfcache restore.
      syncCountFromStorage();

      // Detect a back/forward navigation. event.persisted is the bfcache signal,
      // but iOS Safari frequently reports it false on real restores, so also
      // consult the Performance Navigation API (both the modern and legacy
      // forms) which reports the traversal reliably there.
      var backForward = !!event.persisted;
      try {
        var navEntry =
          performance.getEntriesByType &&
          performance.getEntriesByType("navigation")[0];
        if (navEntry && navEntry.type === "back_forward") {
          backForward = true;
        } else if (
          performance.navigation &&
          performance.navigation.type === 2
        ) {
          backForward = true;
        }
      } catch (e) {}
      if (!backForward) return;

      // On a back/forward nav, if the page was showing a stale count, the frozen
      // page is out of date (count and/or heart-button state). A one-time reload
      // rebuilds everything from the live wishlist — this is what makes it work
      // on Safari, where re-painting the frozen DOM in place isn't reliable. The
      // reload is wrapped in a fade (fadeAndReload) so the customer sees a smooth
      // cross-fade rather than the page visibly reloading under them.
      // Loop-safe: after a reload the navigation type is "reload", not
      // "back_forward", and persisted is false, so this branch won't re-fire.
      var stored = null;
      try {
        stored = localStorage.getItem(WISHLIST_SIG_KEY);
      } catch (e) {}
      if (stored !== null && shownBefore !== null && stored !== shownBefore) {
        fadeAndReload();
      }
    });

    log("init() - WishlistManager initialization complete");
  }

  return {
    init: init,
    addToWishlist: addToWishlist,
    removeFromWishlist: removeFromWishlist,
    checkListExists: checkListExists,
    updateWishlistCount: updateWishlistCount,
    checkSdkReady: checkSdkReady,
    updateButtonState: updateButtonState,
    syncButtonsToVariant: syncButtonsToVariant,
    ensureHash: ensureHash,
    getEffectiveItems: getEffectiveItems,
    isInWishlist: isInWishlist,
  };
})();
